import { useState, useMemo } from 'react';
import * as Popover from '@radix-ui/react-popover';

const EMOJI_OPTIONS = [
  '💬', '📢', '🎯', '🚀', '💡', '🔧', '📋', '📊',
  '🎨', '🎮', '🎵', '📚', '🌍', '🏠', '⚡', '🔥',
  '💎', '🌟', '🎪', '🏗️', '📦', '🎁', '🔒', '🌱',
  '☕', '🍕', '🎬', '📸', '🏆', '🎲', '🧪', '🔬',
];

const EMOJI_LABELS: Record<string, string> = {
  '💬': 'chat', '📢': 'announce', '🎯': 'target', '🚀': 'rocket',
  '💡': 'idea', '🔧': 'tools', '📋': 'board', '📊': 'chart',
  '🎨': 'design art', '🎮': 'game', '🎵': 'music', '📚': 'books',
  '🌍': 'world globe', '🏠': 'home', '⚡': 'fast lightning', '🔥': 'fire hot',
  '💎': 'gem diamond', '🌟': 'star', '🎪': 'circus tent', '🏗️': 'construction build',
  '📦': 'package box', '🎁': 'gift', '🔒': 'lock security', '🌱': 'growth sprout',
  '☕': 'coffee', '🍕': 'pizza food', '🎬': 'film movie', '📸': 'camera photo',
  '🏆': 'trophy award', '🎲': 'dice game', '🧪': 'science lab', '🔬': 'microscope research',
};

const LUCIDE_ICONS = [
  // General
  { name: 'hash', label: 'Hash', path: 'M4 9h16M4 15h16M10 3L8 21M16 3l-2 18' },
  { name: 'message-circle', label: 'Chat', path: 'M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z' },
  { name: 'megaphone', label: 'Announce', path: 'M3 11l18-5v12L3 13v-2z M11.6 16.8a3 3 0 1 1-5.8-1.6' },
  { name: 'target', label: 'Target', path: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z M12 6a6 6 0 1 0 0 12 6 6 0 0 0 0-12z M12 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4z' },
  { name: 'rocket', label: 'Rocket', path: 'M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0 M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5' },
  { name: 'lightbulb', label: 'Idea', path: 'M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5 M9 18h6 M10 22h4' },
  { name: 'wrench', label: 'Tools', path: 'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z' },
  { name: 'clipboard', label: 'Board', path: 'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2 M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z' },
  { name: 'bar-chart', label: 'Chart', path: 'M12 20V10 M18 20V4 M6 20v-4' },
  { name: 'palette', label: 'Design', path: 'M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.93 0 1.5-.75 1.5-1.5 0-.4-.15-.77-.4-1.04-.25-.25-.4-.63-.4-1.04 0-.83.67-1.5 1.5-1.5H16c3.31 0 6-2.69 6-6 0-5.5-4.5-8.92-10-8.92z M8.5 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z M12.5 7.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z M16.5 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z M9 14a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z' },
  { name: 'globe', label: 'World', path: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z M2 12h20 M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z' },
  { name: 'home', label: 'Home', path: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10' },
  { name: 'zap', label: 'Fast', path: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z' },
  { name: 'flame', label: 'Hot', path: 'M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z' },
  { name: 'gem', label: 'Gem', path: 'M6 3h12l4 6-10 13L2 9z M11 3l1 10 M2 9h20 M20 9l-8.5 13 M4 9l8.5 13 M2 9l4-6 M22 9l-4-6' },
  { name: 'star', label: 'Star', path: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z' },
  { name: 'lock', label: 'Lock', path: 'M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2z M7 11V7a5 5 0 0 1 10 0v4' },
  { name: 'sprout', label: 'Growth', path: 'M7 20h10 M10 20c5.5-2.5.8-6.4 3-10 M9.5 9.4c1.1.8 1.8 2.2 2.3 3.7-2 .4-3.5.4-4.8-.3-1.2-.6-2.3-1.9-3-4.2 2.8-.5 4.4 0 5.5.8z M14.1 6a7 7 0 0 0-1.1 4c1.9-.1 3.3-.6 4.3-1.4 1-1 1.6-2.3 1.7-4.6-2.7.1-4 1-4.9 2z' },
  { name: 'coffee', label: 'Coffee', path: 'M17 8h1a4 4 0 1 1 0 8h-1 M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V8z M6 2v2 M10 2v2 M14 2v2' },
  { name: 'package', label: 'Package', path: 'M16.5 9.4l-9-5.19 M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z M3.27 6.96L12 12.01l8.73-5.05 M12 22.08V12' },
  // Communication
  { name: 'mail', label: 'Mail', path: 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6' },
  { name: 'phone', label: 'Phone', path: 'M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z' },
  { name: 'video', label: 'Video', path: 'M23 7l-7 5 7 5V7z M16 3H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z' },
  { name: 'radio', label: 'Radio', path: 'M12 12m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0 M16.24 7.76a6 6 0 0 1 0 8.49 M7.76 16.24a6 6 0 0 1 0-8.49 M19.07 4.93a10 10 0 0 1 0 14.14 M4.93 19.07a10 10 0 0 1 0-14.14' },
  { name: 'bell', label: 'Bell', path: 'M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 0 1-3.46 0' },
  { name: 'send', label: 'Send', path: 'M22 2L11 13 M22 2l-7 20-4-9-9-4 20-7z' },
  // People/Org
  { name: 'users', label: 'Users', path: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75' },
  { name: 'user-plus', label: 'Add User', path: 'M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M8.5 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M20 8v6 M23 11h-6' },
  { name: 'building', label: 'Building', path: 'M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18z M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2 M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2 M10 6h4 M10 10h4 M10 14h4 M10 18h4' },
  { name: 'briefcase', label: 'Briefcase', path: 'M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16' },
  { name: 'crown', label: 'Crown', path: 'M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7z M3 20h18' },
  { name: 'award', label: 'Award', path: 'M12 2a6 6 0 0 0-6 6c0 3.31 2.69 6 6 6s6-2.69 6-6a6 6 0 0 0-6-6z M8.21 13.89L7 23l5-3 5 3-1.21-9.12' },
  // Work
  { name: 'calendar', label: 'Calendar', path: 'M19 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z M16 2v4 M8 2v4 M3 10h18' },
  { name: 'clock', label: 'Clock', path: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z M12 6v6l4 2' },
  { name: 'file-text', label: 'Document', path: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8' },
  { name: 'folder', label: 'Folder', path: 'M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z' },
  { name: 'inbox', label: 'Inbox', path: 'M22 12h-6l-2 3H10l-2-3H2 M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z' },
  { name: 'layers', label: 'Layers', path: 'M12 2L2 7l10 5 10-5-10-5z M2 17l10 5 10-5 M2 12l10 5 10-5' },
  // Dev/Tech
  { name: 'code', label: 'Code', path: 'M16 18l6-6-6-6 M8 6l-6 6 6 6' },
  { name: 'terminal', label: 'Terminal', path: 'M4 17l6-6-6-6 M12 19h8' },
  { name: 'database', label: 'Database', path: 'M12 2C6.48 2 2 3.79 2 6v12c0 2.21 4.48 4 10 4s10-1.79 10-4V6c0-2.21-4.48-4-10-4z M2 6c0 2.21 4.48 4 10 4s10-1.79 10-4 M2 12c0 2.21 4.48 4 10 4s10-1.79 10-4' },
  { name: 'server', label: 'Server', path: 'M2 2h20v8H2z M2 14h20v8H2z M6 6h.01 M6 18h.01' },
  { name: 'cpu', label: 'CPU', path: 'M6 6h12v12H6z M6 9H3 M6 12H3 M6 15H3 M21 9h-3 M21 12h-3 M21 15h-3 M9 6V3 M12 6V3 M15 6V3 M9 21v-3 M12 21v-3 M15 21v-3' },
  { name: 'wifi', label: 'Wifi', path: 'M12 20h.01 M8.53 16.11a6 6 0 0 1 6.95 0 M5.06 12.68a10 10 0 0 1 13.88 0 M1.59 9.25a14 14 0 0 1 20.82 0' },
  // Creative
  { name: 'camera', label: 'Camera', path: 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z M12 13a4 4 0 1 0 0-8 4 4 0 0 0 0 8z' },
  { name: 'music', label: 'Music', path: 'M9 18V5l12-2v13 M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0z M21 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0z' },
  { name: 'film', label: 'Film', path: 'M19.82 2H4.18A2.18 2.18 0 0 0 2 4.18v15.64A2.18 2.18 0 0 0 4.18 22h15.64A2.18 2.18 0 0 0 22 19.82V4.18A2.18 2.18 0 0 0 19.82 2z M7 2v20 M17 2v20 M2 12h20 M2 7h5 M2 17h5 M17 17h5 M17 7h5' },
  { name: 'pen-tool', label: 'Pen', path: 'M12 19l7-7 3 3-7 7-3-3z M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z M2 2l7.586 7.586 M11 13a2 2 0 1 1-4 0 2 2 0 0 1 4 0z' },
  { name: 'scissors', label: 'Scissors', path: 'M6 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M20 4L8.12 15.88 M14.47 14.48L20 20 M8.12 8.12L12 12' },
  { name: 'brush', label: 'Brush', path: 'M9.06 11.9l8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08 M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1.08 1.1 2.49 2.02 4 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 0 0-3-3.02z' },
  // Nature/Other
  { name: 'sun', label: 'Sun', path: 'M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10z M12 1v2 M12 21v2 M4.22 4.22l1.42 1.42 M18.36 18.36l1.42 1.42 M1 12h2 M21 12h2 M4.22 19.78l1.42-1.42 M18.36 5.64l1.42-1.42' },
  { name: 'moon', label: 'Moon', path: 'M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z' },
  { name: 'mountain', label: 'Mountain', path: 'M8 3l4 8 5-5 5 16H2L8 3z' },
  { name: 'tree', label: 'Tree', path: 'M12 22v-7 M17 8l-5-6-5 6h3l-4 5h3l-4 5h14l-4-5h3l-4-5h3z' },
  { name: 'heart', label: 'Heart', path: 'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z' },
  { name: 'map-pin', label: 'Location', path: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z M12 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6z' },
  { name: 'shield', label: 'Shield', path: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' },
  { name: 'flag', label: 'Flag', path: 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z M4 22v-7' },
];

interface IconPickerProps {
  value: string | null;
  onChange: (value: string | null) => void;
}

export function IconPicker({ value, onChange }: IconPickerProps) {
  const [tab, setTab] = useState<'emoji' | 'icons'>('icons');
  const [search, setSearch] = useState('');

  const isLucideIcon = value?.startsWith('lucide:');
  const lucideIconName = isLucideIcon ? value!.slice(7) : null;
  const lucideMatch = lucideIconName ? LUCIDE_ICONS.find(i => i.name === lucideIconName) : null;

  const filteredIcons = useMemo(() => {
    if (!search) return LUCIDE_ICONS;
    const q = search.toLowerCase();
    return LUCIDE_ICONS.filter(i => i.label.toLowerCase().includes(q) || i.name.toLowerCase().includes(q));
  }, [search]);

  const filteredEmojis = useMemo(() => {
    if (!search) return EMOJI_OPTIONS;
    const q = search.toLowerCase();
    return EMOJI_OPTIONS.filter(e => EMOJI_LABELS[e]?.toLowerCase().includes(q));
  }, [search]);

  return (
    <Popover.Root>
      <Popover.Trigger className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-lg transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700">
        {value ? (
          isLucideIcon && lucideMatch ? (
            <svg className="h-5 w-5 text-gray-700 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d={lucideMatch.path} />
            </svg>
          ) : (
            <span>{value}</span>
          )
        ) : (
          <span className="text-gray-400">#</span>
        )}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="z-50 w-72 rounded-xl border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-900"
          sideOffset={8}
          align="start"
        >
          {/* Tabs */}
          <div className="mb-2 flex gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
            <button
              type="button"
              onClick={() => setTab('emoji')}
              className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                tab === 'emoji'
                  ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
              }`}
            >
              Emoji
            </button>
            <button
              type="button"
              onClick={() => setTab('icons')}
              className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                tab === 'icons'
                  ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
              }`}
            >
              Icons
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-2">
            <svg className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (e.target.value && tab === 'emoji') setTab('icons');
              }}
              placeholder="Search icons..."
              className="w-full rounded-md border border-gray-200 bg-gray-50 py-1.5 pl-7 pr-7 text-xs text-gray-900 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Grid */}
          <div className="max-h-48 overflow-y-auto">
            <div className="grid grid-cols-8 gap-1">
              {tab === 'emoji'
                ? filteredEmojis.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => onChange(emoji)}
                      className={`flex h-8 w-8 items-center justify-center rounded-md text-base transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 ${
                        value === emoji ? 'bg-blue-100 ring-2 ring-blue-500 dark:bg-blue-900' : ''
                      }`}
                    >
                      {emoji}
                    </button>
                  ))
                : filteredIcons.map((icon) => (
                    <button
                      key={icon.name}
                      type="button"
                      title={icon.label}
                      onClick={() => onChange(`lucide:${icon.name}`)}
                      className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 ${
                        value === `lucide:${icon.name}` ? 'bg-blue-100 ring-2 ring-blue-500 dark:bg-blue-900' : ''
                      }`}
                    >
                      <svg className="h-4 w-4 text-gray-700 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <path d={icon.path} />
                      </svg>
                    </button>
                  ))}
            </div>
            {((tab === 'icons' && filteredIcons.length === 0) || (tab === 'emoji' && filteredEmojis.length === 0)) && (
              <p className="py-4 text-center text-xs text-gray-400">No matches</p>
            )}
          </div>

          {/* Clear */}
          {value && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="mt-2 w-full rounded-md border border-gray-200 py-1.5 text-xs text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              Remove icon
            </button>
          )}

          <Popover.Arrow className="fill-white dark:fill-gray-900" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

// Standalone renderer for area icons (used in sidebar, headers, etc.)
export function AreaIcon({ icon, iconColor, className = 'h-4 w-4' }: { icon: string | null; iconColor?: string | null; className?: string }) {
  if (!icon) {
    // Default: message-circle icon instead of #
    return (
      <svg className={`${className} text-gray-400 dark:text-gray-500`} style={iconColor ? { color: iconColor } : undefined} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
      </svg>
    );
  }

  if (icon.startsWith('lucide:')) {
    const name = icon.slice(7);
    const match = LUCIDE_ICONS.find(i => i.name === name);
    if (match) {
      return (
        <svg className={`${className} text-gray-600 dark:text-gray-400`} style={iconColor ? { color: iconColor } : undefined} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d={match.path} />
        </svg>
      );
    }
  }

  // Emoji — ignore iconColor for emojis
  return <span className="text-sm">{icon}</span>;
}
